/**
 * Pieces both APR screens share.
 *
 * Application Master Data and Current Run Rate are two views of the same portfolio,
 * so their filter bar, currency formatting and CSV export behave identically. Keeping
 * that here means the two cannot drift apart.
 */

const CURRENCY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

export function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '';
  return CURRENCY.format(value);
}

/**
 * Accounting format for an editable amount: grouped thousands, exactly two decimals,
 * and no currency symbol — `12,345.60`.
 *
 * Separate from `formatCurrency` because this one goes INSIDE an input. A currency
 * symbol in a field the user types into has to be typed around or deleted, and the
 * decimals are the point: an amount that shows `1,200` and another that shows `1,200.5`
 * cannot be compared down a column at a glance.
 */
const ACCOUNTING = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatAccounting(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '';
  return ACCOUNTING.format(value);
}

/**
 * Read a number back out of whatever the user left in the box.
 *
 * MUST be used everywhere a cost draft is read. Once the field holds `12,345.60`,
 * `Number()` returns NaN, and `Number(x) || 0` turns that into a silent ZERO — so a
 * record would save every cost as 0 and the failure would look like data loss rather
 * than a parse bug. Every read site in RunRateDetailDrawer goes through here.
 *
 * Tolerant on input: strips grouping, currency symbols and spaces, keeps a leading
 * minus and one decimal point.
 */
export function parseAmount(raw: string | number | null | undefined): number {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : 0;
  if (raw === null || raw === undefined) return 0;
  const cleaned = String(raw).replace(/[^0-9.-]/g, '');
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : 0;
}

/**
 * The four structured filters, matching APR's filter row.
 *
 * Free text rather than dropdowns, exactly as APR has them: the values are long and
 * inconsistent, and typing a fragment beats scrolling a list of five hundred.
 */
export const STRUCTURED_FILTERS = [
  { key: 'vendor', label: 'vendor', field: 'vendor_supplier' },
  { key: 'businessFunction', label: 'business function', field: 'business_function_domain' },
  { key: 'applicationType', label: 'application type', field: 'application_type' },
  { key: 'businessCriticality', label: 'business criticality', field: 'business_criticality' },
] as const;

export type StructuredFilterKey = (typeof STRUCTURED_FILTERS)[number]['key'];

export type FilterValues = Record<StructuredFilterKey, string>;

export const EMPTY_FILTERS: FilterValues = {
  vendor: '',
  businessFunction: '',
  applicationType: '',
  businessCriticality: '',
};

export function hasActiveFilter(text: string, filters: FilterValues): boolean {
  return text.trim() !== '' || Object.values(filters).some((v) => v !== '');
}

/**
 * Abbreviated money, for the statistics strip: `$106.6M`.
 *
 * APR's stat cards do not print the full figure — at portfolio scale it is unreadable
 * and the point of the strip is a glance. Cells still show the exact amount.
 */
const COMPACT_CURRENCY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

export function formatCompactCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '$0';
  return COMPACT_CURRENCY.format(value);
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return value.toLocaleString('en-US');
}

/** Case-insensitive contains, for one structured filter against one field. */
export function matchesFilter(value: string, field: string | null | undefined): boolean {
  if (!value.trim()) return true;
  return (field ?? '').toLowerCase().includes(value.trim().toLowerCase());
}

/** Case-insensitive "does any of these fields contain the search text". */
export function matchesText(text: string, fields: readonly (string | number | null)[]): boolean {
  const needle = text.trim().toLowerCase();
  if (!needle) return true;
  return fields.some((field) =>
    field === null || field === undefined
      ? false
      : String(field).toLowerCase().includes(needle),
  );
}

export function csvCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  const text = String(value);
  // Quote anything a spreadsheet would otherwise mis-split.
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function toCsv(
  columns: readonly { header: string; value: (row: never) => unknown }[],
  rows: readonly unknown[],
): string {
  const head = columns.map((c) => csvCell(c.header)).join(',');
  const body = rows.map((row) =>
    columns.map((c) => csvCell((c.value as (r: unknown) => unknown)(row))).join(','),
  );
  return [head, ...body].join('\r\n');
}

/**
 * Parses a CSV into rows keyed by header.
 *
 * Written out rather than pulled from a library because the input is one known
 * shape — our own exported template, usually after a trip through Excel. It handles
 * quoted fields, escaped quotes and CRLF, which is what that trip actually produces.
 */
export function parseCsv(input: string): Record<string, string>[] {
  // Excel writes a UTF-8 BOM; left in place it becomes part of the first header.
  const text = input.replace(/^\uFEFF/, '');

  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i]!;

    if (quoted) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else {
          quoted = false;
        }
      } else {
        cell += char;
      }
      continue;
    }

    if (char === '"') {
      quoted = true;
    } else if (char === ',') {
      row.push(cell);
      cell = '';
    } else if (char === '\n') {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else if (char !== '\r') {
      cell += char;
    }
  }

  if (cell !== '' || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }

  const header = rows.shift();
  if (!header) return [];

  const keys = header.map((h) => h.trim());
  return rows
    // A trailing newline leaves one empty row; so does a blank line mid-file.
    .filter((cells) => cells.some((c) => c.trim() !== ''))
    .map((cells) => {
      const record: Record<string, string> = {};
      keys.forEach((key, index) => {
        record[key] = (cells[index] ?? '').trim();
      });
      return record;
    });
}

/**
 * Prompts for a file and returns its text.
 *
 * Resolves to null when the picker is dismissed, so a cancelled upload is an
 * ordinary outcome rather than an error the screen has to explain.
 */
export function pickTextFile(accept = '.csv,text/csv'): Promise<string | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      if (!file) {
        resolve(null);
        return;
      }
      file
        .text()
        .then(resolve)
        .catch(() => resolve(null));
    });
    // Firefox will not open a picker for an element outside the document.
    input.style.display = 'none';
    document.body.appendChild(input);
    input.click();
    document.body.removeChild(input);
  });
}

/**
 * Hands a file to the browser without a server round trip.
 *
 * A BOM is prepended so Excel opens the file as UTF-8 rather than mangling any
 * non-ASCII application name.
 */
export function downloadFile(filename: string, content: string, mime = 'text/csv'): void {
  const blob = new Blob([`\uFEFF${content}`], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}


/**
 * Header-tolerant CSV column matching.
 *
 * A spreadsheet that has been round-tripped through Excel, or typed by hand, will not
 * carry the exact header the template emitted — "Application ID", "App ID", "ID" and
 * "ApplicationID" all mean the same column. Matching by a set of accepted names rather
 * than by position or exact string is what makes an upload usable.
 *
 * Comparison ignores case, spaces, and punctuation, so "App Owner Email",
 * "app_owner_email" and "AppOwnerEmail" all collapse to the same key.
 */
export interface ColumnSpec {
  /** The field name the API expects. */
  readonly field: string;
  /** Every header that means this column. The first is what the template emits. */
  readonly accepts: readonly string[];
}

const normalise = (text: string) => text.toLowerCase().replace(/[^a-z0-9]/g, '');

/** Maps one parsed CSV row onto API field names, ignoring columns we do not know. */
export function mapRow(
  row: Record<string, string>,
  columns: readonly ColumnSpec[],
): Record<string, string> {
  const byNormalised = new Map<string, string>();
  for (const [header, value] of Object.entries(row)) {
    byNormalised.set(normalise(header), value);
  }

  const mapped: Record<string, string> = {};
  for (const column of columns) {
    for (const accepted of column.accepts) {
      const value = byNormalised.get(normalise(accepted));
      if (value !== undefined) {
        mapped[column.field] = value;
        break;
      }
    }
  }
  return mapped;
}

/** True when the file carries at least one header meaning `field`. */
export function hasColumn(
  row: Record<string, string> | undefined,
  column: ColumnSpec,
): boolean {
  if (!row) return false;
  const present = new Set(Object.keys(row).map(normalise));
  return column.accepts.some((accepted) => present.has(normalise(accepted)));
}

/** `application_master_data_2026-07-27.csv` — dated, so exports do not overwrite. */
export function datedFilename(stem: string): string {
  const now = new Date();
  const date = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');
  return `${stem}_${date}.csv`;
}

/**
 * Does a cost record's total agree with the sum of its five components?
 *
 * A penny of tolerance, as APR uses: these are separately-sourced figures and floating
 * point will not land exactly. A real gap is a finding, not a rounding artefact.
 */
export const RECONCILE_TOLERANCE = 0.01;

export function componentSum(record: {
  annual_software_licensing_fees: number;
  annual_third_party_vendor_support: number;
  annual_infrastructure_cost: number;
  indirect_operational_costs: number;
  cloud_saas_subscription_cost_impact: number;
}): number {
  return (
    (record.annual_software_licensing_fees || 0) +
    (record.annual_third_party_vendor_support || 0) +
    (record.annual_infrastructure_cost || 0) +
    (record.indirect_operational_costs || 0) +
    (record.cloud_saas_subscription_cost_impact || 0)
  );
}

export function isReconciled(record: Parameters<typeof componentSum>[0] & {
  total_annual_cost: number;
}): boolean {
  const sum = componentSum(record);
  // Nothing entered against the components yet is not a mismatch, just unfilled.
  if (sum === 0) return true;
  return Math.abs((record.total_annual_cost || 0) - sum) <= RECONCILE_TOLERANCE;
}


/**
 * Builds a CSV from the column contract: header row, then one row per record.
 *
 * Template and export share this, so a file downloaded as a template and a file
 * downloaded as data always have identical headers — which is what lets the export be
 * edited and uploaded straight back.
 */
export function csvFromColumns(
  columns: readonly ColumnSpec[],
  records: readonly Record<string, unknown>[],
): string {
  const header = columns.map((c) => c.accepts[0]!);
  const rows = records.map((record) => columns.map((c) => record[c.field]));
  return [header, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n');
}
